import React, { useEffect, useMemo, useState } from 'react';

import { Stack, Typography } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';
import { useTranslation } from 'react-i18next';

import { paths } from 'routes';

import { BreadCrumbs } from 'components/Breadcrumbs';
import { ContentLayout } from 'website/components/Layout/components/ContentLayout';
import { Pagination } from 'components/Pagination';
import { Search } from 'components/Search';

import { useAuth } from 'lib/auth';
import { Loader } from 'components/Loader';
import { NotFound } from 'components/NotFound/NotFound';
import { ListItem } from '../components/BlogListItem';
import { useGetAllBlogs } from '../api/getAllBlogs';
import { PaperCard } from 'components/Card';
import { Subscribe } from 'website/components/SubscribeSection';
import { useLocation } from 'react-router-dom';
import BackgroundImage from '../assets/background.png';

export const List = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialSearch = queryParams.get('search') || '';

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(initialSearch);

  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(null);

  const websiteContext = location?.state?.websiteContext || 'suppliers';

  const {
    data: { blogs, total },
    refetch: refetchBlogs,
    isFetching,
  } = useGetAllBlogs({
    search,
    page: page - 1,
    queryOptions: {
      onSettled: () => {
        clearTimeout(timer);
        setTimeout(() => setLoading(false), 1000);
      }
    },
  });

  const handlePageChange = (_, page) => setPage(page);
  const debouncedSearchChange = useDebouncedCallback((search) => setSearch(search), 300);
  const handleSearchChange = ({ target: { value: search } }) => {
    debouncedSearchChange(search);
  };

  useRefetch();
  
  const caption = /* account.super_admin ? 'Click the "Add Blog" button to begin' :  */'';

  useEffect(() => {
    if (isFetching) {
      const timeoutId = setTimeout(() => setLoading(true), 500);
      setTimer(timeoutId);
    } else {
      clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, [isFetching]);

  return (
    <ContentLayout>
      <Stack height={1} width={1} sx={{ alignSelf: "center"}} /* gap={1} */>
        <Stack
          pb={2}
          bgcolor="background.paper"
          height="100%"
        >
          <Stack
            direction="column"
            gap={6.25}
            alignItems="center"
            justifyContent={"space-between"}
            pt={6.25}
            sx={{
              backgroundImage: `url(${BackgroundImage})`,
              backgroundColor: 'grey.300',
              backgroundPosition: 'center',
              backgroundSize: "cover",
              backgroundRepeat: 'no-repeat',
              position: "relative",
              ml: "-50vw",
              left: "50%",
              width: "100vw",
              minHeight: 300,
              height: {xs: 1, md: 1},
            }}
            minWidth={256}
          >
            <Stack
              direction="column"
              gap={2.5}
              alignItems="center"
              justifyContent={"space-between"}
              width={1}
              maxWidth={{xs:664, md: 1200}}
              sx={{
                boxSizing: 'border-box',
                px: {xs: 4, md: 6},
              }}
            >
              <Search
                placeholder={t('website:blogList.searchPlaceholder')}
                handleSearchChange={handleSearchChange}
                initialValue={initialSearch}
                sx={{
                  minWidth: 200,
                  height: 40,
                  py: 2.5,
                  px: 3.5,
                  borderRadius: 1,
                  alignSelf: "stretch",
                  border: "1px solid",
                  borderColor:"#13373F33",
                  backgroundColor: "background.paper",
                  boxShadow: "0 12px 40px 0 rgba(0, 0, 0, 0.08)",
                }}
                inputSx={{
                  border: "none",
                  height: 1,
                  '& .MuiOutlinedInput-input': {
                    fontSize: "1rem",
                    fontWeight: 400,
                    fontFeatureSettings: `'liga' off, 'clig' off`,
                    color: 'text.black',
                    lineHeight: '24px',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: "text.placeholder",
                    fontFeatureSettings: '"liga" off, "cling" off',
                    fontSize: '1rem',
                    fontWeight: 400,
                    lineHeight: '24px',
                  },
                  pl: 0,
                  pr: 0,
                }}
                iconSx={{
                  svg: {
                    height: 24,
                    color: 'text.black',
                    stroke: "#000",
                    strokeWidth: 0,
                  }
                }}
              />
              <Subscribe />
            </Stack>
          </Stack>
        </Stack>

        <Stack
          px={4}
          pb={2}
          height="100%"
          alignSelf={"center"}
          alignItems={{xs: "center", md: "flex-start"}}
          width={1}
          boxSizing={"border-box"}
        >
          {/* <Stack direction="row" gap={2} alignItems="center" justifyContent={"end"}>
          </Stack> */}

          {isFetching && !loading ? 
          (
            <></>
          ) :
          (!isFetching || (isFetching && loading)) ?
          (
            <>
            {loading && (
              <Loader style={{ marginTop: "20vh"}}/>
            )}
          
            {!loading && (
            <>
            {!blogs && (
              <Stack
                height="100%"
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="h5">{t('website:blogList.noBlogsAdded')}</Typography>
                {/* {account.super_admin && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      letterSpacing: '-0.14px',
                    }}
                  >
                    Click the "Add Blog" button to begin
                  </Typography>
                )} */}
              </Stack>
            )}

            {blogs && (
              <>
                <Stack
                  flex={1}
                  mt={{xs:0, md:2}}
                  py={2}
                  bgcolor= "background.paper"
                  borderTop= {blogs.length !== 0 ? {xs: "none", md: "1px solid"} : "none"}
                  borderColor = {{xs: "border.main", md: "border.main"}}
                  gap={1}
                  maxWidth={{xs: 366, md: "none"}}
                  width={1}
                >
                  {/* {blogs.length === 0 && (
                    <NotFound
                      wrapperSx={{ height: 1, justifyContent: "center" }}
                      title="No Blogs found"
                      caption={caption}
                    />
                  )} */}
                  {/* {blogs.length !== 0 &&
                    blogs.map((item) => (
                      <ListItem key={item.blog_id} {...item} />
                    ))
                  } */}
                  {blogs.length > 0 ? (
                    <Stack direction="column" gap={2}>
                      {blogs
                        .reduce((rows, item, index) => {
                          if (index % 3 === 0) {
                            rows.push([]);
                          }
                          rows[rows.length - 1].push(<ListItem key={item.blog_id} {...item} websiteContext={websiteContext} />);
                          console.log(rows);
                          
                          return rows;
                        }, [])
                        .map((row, rowIndex, allRows) => {
                          const itemsInRow = row.length;
                          const emptySlotsNeeded = 3 - itemsInRow;                          
            
                          return (
                            <Stack key={rowIndex} direction={{xs: "column", md: "row"}} gap={2}>
                              {row.map((item) => item)}
                              {rowIndex === allRows.length - 1 && emptySlotsNeeded > 0 && (
                                Array.from({ length: emptySlotsNeeded }).map((_, index) => (
                                  <PaperCard
                                    sx={{
                                      flex: 1,
                                      boxShadow: 0,
                                      borderRadius: 1,
                                      p: 2,
                                      visibility: "hidden",
                                      display: {xs: "none", md: "flex"}
                                    }}
                                    key={`empty-${rowIndex}-${index}`}
                                    flex={1}
                                  >
                                    <></>
                                  </PaperCard>
                                ))
                              )}
                            </Stack>
                          );
                        })}
                    </Stack>
                  ) : (
                    <NotFound
                      wrapperSx={{ height: 1, justifyContent: "center", mt: "20vh" }}
                      title={t('website:blogList.emptyState.title')}
                      caption={caption}
                    />
                  )}
                </Stack>
              </>
            )}

          {blogs.length > 0 && (
            <Stack direction="row" alignSelf="center" justifyContent="center" sx={{my: 4}} display={{xs: "none", md: "flex"}}>
              <Pagination
                page={page}
                handlePageChange={handlePageChange}
                itemCount={total}
                itemsPerPage={6}
                prevLabel={t('website:common.pagination.prev')}
                nextLabel={t('website:common.pagination.next')}
              />
            </Stack>
          )}
            </>
            )}
            </>
          ) :
          (
            <></>
          )}
        </Stack>
      </Stack>
    </ContentLayout>
  );

  function useRefetch() {
    useEffect(() => {
      refetchBlogs();
    }, [page, search]);
  }
};
